import { useFeatureValue } from '@growthbook/growthbook-react'
import { ISize, mapSizesToProps, Optional } from '@hg/stylizator/utils'
import * as React from 'react'
import { useCookies } from 'react-cookie'
import withSizes from 'react-sizes'

import ClientOnly from '../ClientOnly'

import { IFields, initialFields } from 'app/components/SignupCampaign/Fields'
import GiftButton from 'app/components/SignupCampaign/GiftButton'
import SignupCard from 'app/components/SignupCampaign/SignupCard'
import OnLifecycle from 'app/helpers/OnLifecycle'
import useHasMounted from 'app/hooks/use-has-mounted'
import { useGlobalState } from 'app/store'

const SignupModal = React.lazy(() => import('app/components/SignupCampaign/SignupModal'))

interface IProps {
  url: string
}

const SignupBanner: React.FC<IProps & ISize> = (props) => {
  const [cookies, setCookie] = useCookies(['signupBanner'])
  const signupBannerFields: Optional<IFields> = cookies.signupBanner
  const [fields, setFields] = React.useState<IFields>(signupBannerFields ? signupBannerFields : initialFields)
  const [modalVisible, setModalVisible] = React.useState(fields.modalClosed)
  const hasMounted = useHasMounted()

  const [blocks] = useGlobalState('reusableBlocks')
  const signupBanner = blocks.get('honeygain-components/signup-campaign')

  const modalVsHelloBarExperiment = useFeatureValue('modal-vs-hello-bar', 'modal')
  const isModalExperimentEnabled = modalVsHelloBarExperiment === 'modal'

  if (!signupBanner || !hasMounted) {
    return null
  }

  if (signupBanner?.attrs?.enabled === false) {
    return null
  }

  if (!isModalExperimentEnabled) {
    return null
  }

  const handleScroll = (): void => {
    if (window.scrollY > 20) {
      if (!fields.modalClosed) {
        setModalVisible(true)
      }
      window.removeEventListener('scroll', handleScroll)
    }
  }

  return (
    <OnLifecycle
      onEnter={() => {
        window.addEventListener('scroll', handleScroll)
      }}
    >
      <GiftButton
        onClick={() => {
          if (props.size === 'mobile') {
            setFields((prevState) => ({ ...prevState, modalClosed: false }))
            setModalVisible(true)
          } else {
            setFields((prevState) => ({ ...prevState, cardClosed: false }))
          }
        }}
      />
      {!fields.cardClosed && (!modalVisible || fields.modalClosed) && props.size !== 'mobile' && (
        <SignupCard
          onClose={() => {
            setFields((prevState) => ({ ...prevState, cardClosed: true }))

            const future = new Date()
            future.setDate(future.getDate() + 30)

            setCookie(
              'signupBanner',
              { ...fields, cardClosed: true },
              {
                expires: future,
                path: '/',
              },
            )
          }}
          heading={signupBanner.attrs.cardHeading}
          content={signupBanner.attrs.cardContent}
          buttonText={signupBanner.attrs.cardButtonText}
          buttonUrl={props.url}
        />
      )}
      {!fields.modalClosed && modalVisible && (
        <ClientOnly>
          <SignupModal
            onClose={() => {
              setFields({
                modalClosed: true,
                cardClosed: false,
              })
              setModalVisible(false)

              const future = new Date()
              future.setDate(future.getDate() + 30)

              setCookie(
                'signupBanner',
                { ...fields, modalClosed: true },
                {
                  expires: future,
                  path: '/',
                },
              )
            }}
            heading={signupBanner.attrs.modalHeading}
            content={signupBanner.attrs.modalContent}
            buttonText={signupBanner.attrs.modalButtonText}
            buttonUrl={props.url}
          />
        </ClientOnly>
      )}
    </OnLifecycle>
  )
}

export default withSizes<ISize, IProps & ISize>(mapSizesToProps)(SignupBanner)
